import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  User, 
  Mail, 
  Building, 
  Shield, 
  Calendar,
  Monitor,
  Globe,
  Clock,
  LogOut,
  Trash2,
  AlertTriangle
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/use-toast';
import { useAuthStore } from '@/store/authStore';

import api from '@/services/api';

const ProfilePage = () => {
  const { user, logout } = useAuthStore();
  const queryClient = useQueryClient();
  const [showSessions, setShowSessions] = useState(false);

  // Fetch user sessions
  const { data: sessions, isLoading: sessionsLoading } = useQuery({
    queryKey: ['user-sessions'],
    queryFn: () => api.get('/auth/sessions').then(res => res.data.data.sessions),
    enabled: showSessions
  });

  // Invalidate specific session mutation
  const invalidateSessionMutation = useMutation({
    mutationFn: (sessionId) => api.delete(`/auth/sessions/${sessionId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['user-sessions']);
      toast({
        title: "Başarılı",
        description: "Oturum başarıyla sonlandırıldı.",
      });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.error || "Oturum sonlandırılamadı",
        variant: "destructive"
      });
    }
  });

  // Invalidate all sessions mutation
  const invalidateAllSessionsMutation = useMutation({
    mutationFn: () => api.delete('/auth/sessions'),
    onSuccess: () => {
      queryClient.invalidateQueries(['user-sessions']);
      toast({
        title: "Başarılı",
        description: "Tüm oturumlar sonlandırıldı. Yeniden giriş yapmanız gerekecek.",
      });
      // Logout user after invalidating all sessions
      setTimeout(() => {
        logout();
      }, 2000);
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.error || "Oturumlar sonlandırılamadı",
        variant: "destructive"
      });
    }
  });

  const handleInvalidateSession = (sessionId) => {
    if (window.confirm('Bu oturumu sonlandırmak istediğinizden emin misiniz?')) {
      invalidateSessionMutation.mutate(sessionId);
    }
  };

  const handleInvalidateAllSessions = () => {
    if (window.confirm('Tüm oturumları sonlandırmak istediğinizden emin misiniz? Bu işlem sizi de sistemden çıkaracaktır.')) {
      invalidateAllSessionsMutation.mutate();
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('tr-TR');
  };

  const getDeviceInfo = (userAgent) => {
    if (!userAgent) return 'Bilinmeyen Cihaz';
    
    // Simple device detection
    if (userAgent.includes('Mobile')) return '📱 Mobil Cihaz';
    if (userAgent.includes('Tablet')) return '📱 Tablet';
    if (userAgent.includes('Chrome')) return '🌐 Chrome';
    if (userAgent.includes('Firefox')) return '🦊 Firefox';
    if (userAgent.includes('Safari')) return '🍎 Safari';
    if (userAgent.includes('Edge')) return '🌐 Edge';
    
    return '💻 Bilgisayar';
  };

  const roleLabels = {
    admin: 'Yönetici',
    supervisor: 'Süpervizör',
    team_leader: 'Ekip Lideri',
    operator: 'Operatör',
    paramedic: 'Paramedik'
  };

  const roleColors = {
    admin: 'bg-red-100 text-red-800',
    supervisor: 'bg-purple-100 text-purple-800',
    team_leader: 'bg-blue-100 text-blue-800',
    operator: 'bg-green-100 text-green-800',
    paramedic: 'bg-yellow-100 text-yellow-800'
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight mb-2">Profil</h1>
        <p className="text-muted-foreground">
          Hesap bilgilerinizi ve oturum durumunuzu yönetin
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <User className="h-5 w-5 mr-2" />
              Kullanıcı Bilgileri
              </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-3">
              <Mail className="h-4 w-4 text-gray-500" />
              <span className="font-medium">{user?.email}</span>
                </div>
            
            <div className="flex items-center space-x-3">
              <User className="h-4 w-4 text-gray-500" />
              <span className="font-medium">
                {user?.first_name} {user?.last_name}
              </span>
                </div>
            
                <div className="flex items-center space-x-3">
              <Building className="h-4 w-4 text-gray-500" />
              <span className="font-medium">{user?.institution_name}</span>
                  </div>
            
                <div className="flex items-center space-x-3">
              <Shield className="h-4 w-4 text-gray-500" />
              <Badge className={roleColors[user?.role]}>
                {roleLabels[user?.role]}
              </Badge>
                  </div>
            
                <div className="flex items-center space-x-3">
              <Calendar className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-600">
                Üye olma: {formatDate(user?.created_at)}
              </span>
              </div>
          </CardContent>
        </Card>

        {/* Security Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Shield className="h-5 w-5 mr-2" />
              Güvenlik
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Şifre Değiştir</span>
              <Button variant="outline" size="sm">
                Değiştir
              </Button>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">İki Faktörlü Doğrulama</span>
              <Badge variant="secondary">Aktif Değil</Badge>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Oturum Yönetimi</span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowSessions(!showSessions)}
              >
                {showSessions ? 'Gizle' : 'Göster'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sessions Management */}
      {showSessions && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center">
                <Monitor className="h-5 w-5 mr-2" />
                Aktif Oturumlar
              </div>
                <Button
                variant="destructive"
                  size="sm"
                onClick={handleInvalidateAllSessions}
                disabled={invalidateAllSessionsMutation.isPending}
              >
                {invalidateAllSessionsMutation.isPending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <>
                    <LogOut className="h-4 w-4 mr-2" />
                    Tümünü Sonlandır
                  </>
                )}
                  </Button>
            </CardTitle>
            <CardDescription>
              Tüm cihazlardaki aktif oturumlarınızı görüntüleyin ve yönetin
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sessionsLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-sm text-gray-600">Oturumlar yükleniyor...</p>
              </div>
            ) : sessions && sessions.length > 0 ? (
              <div className="space-y-3">
                {sessions.map((session) => (
                  <div key={session.session_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">
                        {getDeviceInfo(session.device_info)}
                </div>
                <div>
                        <div className="font-medium text-sm">
                          {session.ip_address}
                        </div>
                        <div className="text-xs text-gray-500">
                          Son aktivite: {formatDate(session.last_activity)}
                        </div>
                      </div>
                </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleInvalidateSession(session.session_id)}
                      disabled={invalidateSessionMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <AlertTriangle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Aktif oturum bulunamadı</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProfilePage; 